(() => {
// This version is for use without es6 modules
// Dependencies: slider-factory.js, pager-factory.js, utils.js
/**
 * @file slider-factory.js is responsible for creating and rendering the slider.
 * @module sliderFactory
 */ /**
 * Factory function to create a slider object with methods to initialize, move
 * to previous/next slide, check button states, add listeners, show/hide
 * buttons, get gap and items to show, and check pager.
 * @namespace slider
 * @property {Object} responsive - Object containing responsive options for
 * the slider.
 * @property {HTMLElement} slider - The slider element.
 * @property {HTMLElement} sliderInner - The inner slider element containing
 * the slides.
 * @property {NodeList} elements - The list of slide elements.
 * @property {HTMLElement} buttons - The slider navigation buttons element.
 * @property {HTMLElement} prevButton - The previous slide button element.
 * @property {HTMLElement} nextButton - The next slide button element.
 * @property {HTMLElement} pagerEl - The pager element.
 * @property {string} hiddenClass - The class name to hide the navigation
 * buttons.
 * @property {Object} pager - The pager object with methods to render and set
 * active pager item.
 * @property {function} init - Method to initialize the slider with the given
 * slider element and responsive options.
 * @property {function} moveToPrevious - Method to move to the previous slide.
 * @property {function} moveToNext - Method to move to the next slide.
 * @property {function} checkButtons - Method to check the button states
 * (disabled or enabled).
 * @property {function} addListeners - Method to add event listeners for scroll,
 * keyboard navigation, load and resize.
 * @property {function} showHideButtons - Method to show or hide the navigation
 * buttons based on the container width and total width of the slides.
 * @property {function} getGap - Method to get the gap between the slides.
 * @property {function} getItemsToShow - Method to get the number of items to
 * show based on the responsive options.
 * @property {function} checkPager - Method to check the pager and set the
 * active pager item.
 */ /**
 * Returns the current responsive options based on the current viewport
 *
 * @param {Object} responsive - Object containing responsive options
 * @return {Object}
 */ const $97ae949e493c6975$export$9266a1443ac0612 = (responsive)=>{
    const viewportWidth = window.visualViewport.width;
    let responsiveItems = Object.keys(responsive).filter((key)=>key < viewportWidth);
    responsiveItems = responsiveItems.map((i)=>Number(i));
    const key = responsiveItems.length > 0 ? Math.max(...responsiveItems) : Math.min(...Object.keys(responsive).map(Number));
    return responsive[key];
};
/**
 * Debounce function that returns a promise
 *
 * @param {Function} func
 * @param {Number} delay
 * @return {Function}
 */ function $97ae949e493c6975$export$45ee3b73b8783988(func, delay) {
    let timeoutId;
    return function(...args) {
        const context = this;
        clearTimeout(timeoutId);
        return new Promise((resolve)=>{
            timeoutId = setTimeout(()=>{
                resolve(func.apply(context, args));
            }, delay);
        });
    };
}
/**
 * Checks whether HTML element appears with the visible part of the
 * visible portion of the slider
 *
 * @param {HTMLElement} element
 * @param {HTMLElement} parent - The slider element
 * @return {Boolean}
 */ const $97ae949e493c6975$var$_withinViewport = (element, parent)=>{
    const elementRect = element.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();
    return elementRect.left >= parentRect.left && elementRect.left <= parentRect.right;
};
/**
 * Returns the slider item at the given index.
 * @param {string|number} index - The index of the slider item to retrieve.
 * @param {Array} elements - The array of slider items.
 * @return {HTMLElement} - The slider item at the given index.
 */ const $97ae949e493c6975$var$_getSliderItem = (index, elements)=>{
    if (!elements || elements.length === 0) return null;
    if (typeof index === 'string') index = parseInt(index, 10);
    if (isNaN(index) || index < 0 || index >= elements.length) return null;
    return elements[index];
};
/**
   * Calculate the width of an item based on the available space and the number
   * of items to show (excluding the css gap spacing)
   *
   * @param {Number} width - the visible width of the slider
   * @param {Number} itemsToShow - the number of items that should be visible
   * @param {Number} gap - this css gap property width
   * @return {Number}
   */ const $97ae949e493c6975$var$_getItemSize = function(width, itemsToShow, gap) {
    const tempWidth = width - gap * (itemsToShow - 1);
    return tempWidth / itemsToShow;
};
/**
 *
 * @param {NodeList} elements
 * @param {Number} itemWidth
 */ function $97ae949e493c6975$var$_setElementWidths(elements, itemWidth) {
    elements.forEach((item)=>{
        item.style.flexBasis = itemWidth + 'px';
    });
}
const $97ae949e493c6975$var$_makePositions = function(elements, elementWidth) {
    const positions = [];
    for(let index = 0; index < elements.length; index++)positions.push(elementWidth * index);
    return positions;
};
const $97ae949e493c6975$var$_scrollToNext = function(container, gutter, positions) {
    let scrollPosition = container.scrollLeft;
    scrollPosition = scrollPosition + container.clientWidth - gutter;
    const filteredPositions = positions.filter((i)=>i >= scrollPosition);
    if (filteredPositions.length == 0) return scrollPosition;
    else return Math.min(...filteredPositions);
};
const $97ae949e493c6975$var$_scrollToPrevious = function(container, gutter, positions) {
    let scrollPosition = container.scrollLeft;
    scrollPosition = scrollPosition - container.clientWidth + gutter;
    const filteredPositions = positions.filter((i)=>i <= scrollPosition);
    if (filteredPositions.length == 0) return 0;
    else return Math.max(...filteredPositions);
};
/**
   * Takes a container element and a set of slider elements (which should all be
   * the same width). From this we can calculate the width of the container and
   * we can then work out what the nearest next or previous item if we are to
   * scroll the slider by the width of the container.
   *
   * @param {HTMLElement} container
   * @param {NodeList} elements
   * @param {String} direction
   * @param {Number} gutter need the gutter width
   * @return {Number}
   */ function $97ae949e493c6975$var$_getScrollTo(container, elements, direction, gutter) {
    // let scrollPosition = container.scrollLeft;
    const elementWidth = elements[0].clientWidth + gutter;
    const positions = $97ae949e493c6975$var$_makePositions(elements, elementWidth);
    let scrollTo = 0;
    switch(direction){
        case 'next':
            scrollTo = $97ae949e493c6975$var$_scrollToNext(container, gutter, positions);
            break;
        case 'previous':
            scrollTo = $97ae949e493c6975$var$_scrollToPrevious(container, gutter, positions);
            break;
        default:
            break;
    }
    return scrollTo;
}
/**
 * Generic function to drive the slide. Just needs a direction (previous or
 * next) and will do the rest of the heavy lifting.
 *
 * @param {String} direction
 * @param {HTMLElement} sliderInner
 * @param {Object} responsive
 */ const $97ae949e493c6975$export$c1742949d0193489 = function(direction, sliderInner, gap) {
    if (!sliderInner) return;
    const els = sliderInner.querySelectorAll('.slider-item');
    if (!els || els.length === 0) return;
    const scrollPosition = $97ae949e493c6975$var$_getScrollTo(sliderInner, els, direction, gap);
    sliderInner.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
    });
};
/**
 *
 * @param {*} start
 * @param {*} end
 * @param {*} els
 * @param {*} dataLeft
 * @param {*} i
 * @return {HTMLElement}
 */ function $97ae949e493c6975$export$dc35b5db7ba3f005(start, end, els, dataLeft, i) {
    if (end > els.length) end = els.length;
    end = end - 1;
    const d = document.createElement('button');
    d.classList.add('slider-pager-item');
    d.setAttribute('data-page-start', start);
    d.setAttribute('data-page-end', end);
    d.setAttribute('data-scrollto', dataLeft);
    d.type = 'button';
    d.innerHTML = `<span>${i + 1}</span>`;
    return d;
}
/**
 * Checks if the item is active
 *
 * @param {HTMLElement} item
 * @param {NodeList} elements
 * @param {Boolean} activeIsSet
 * @param {HTMLElement} slider
 * @return {Boolean}
 */ function $97ae949e493c6975$export$39e04e72f972b885(item, elements, activeIsSet, slider) {
    const itemNo = item.getAttribute('data-page-end');
    const innerItem = $97ae949e493c6975$var$_getSliderItem(itemNo, elements);
    if (!activeIsSet && innerItem && $97ae949e493c6975$var$_withinViewport(innerItem, slider)) {
        item.classList.add('active');
        activeIsSet = true;
    }
    return activeIsSet;
}
/**
 * Gets the button element
 *
 * @param {HTMLElement} sliderInner
 * @param {HTMLElement} prevButton
 * @param {HTMLElement} nextButton
 */ function $97ae949e493c6975$export$fb18650d142de48d(sliderInner, prevButton, nextButton, btnClick = false) {
    if (prevButton) {
        if (sliderInner.scrollLeft < 50) {
            prevButton.setAttribute('disabled', true);
            // set focus to the next button
            if (btnClick) nextButton.focus();
        } else prevButton.removeAttribute('disabled');
    }
    const scrollMax = sliderInner.scrollWidth - sliderInner.clientWidth;
    if (nextButton) {
        if (sliderInner.scrollLeft >= scrollMax - 50) {
            nextButton.setAttribute('disabled', true);
            // set focus to the previous button
            if (btnClick) prevButton.focus();
        } else nextButton.removeAttribute('disabled');
    }
}
/**
 * This function returns the slider object so that it can be used in a
 * promise chain.
 *
 * @param { Object } slider
 * @return { Object }
 */ const $97ae949e493c6975$export$8382a6a3b017d9e2 = function(slider) {
    const containerWidth = slider.sliderInner.clientWidth;
    const gap = slider.getGap();
    const itemWidth = $97ae949e493c6975$var$_getItemSize(containerWidth - slider.getPreview(), slider.getItemsToShow(), gap);
    slider.sliderInner.style.gap = '0 ' + gap + 'px';
    $97ae949e493c6975$var$_setElementWidths(slider.elements, itemWidth);
    slider.tempTotalWidth = itemWidth * slider.elements.length + gap * (slider.elements.length - 1);
    return slider;
};
/**
 * Handle the pager click event
 *
 * @param {HTMLElement} element
 * @param {HTMLElement} slider
 * @param {HTMLElement} sliderInner
 */ function $97ae949e493c6975$export$43c35f60672766ee(element, slider, sliderInner) {
    if (!element || !slider || !sliderInner) return;
    // Defensive: ensure we have the correct element
    const pagerItem = element.closest('.slider-pager-item');
    if (!pagerItem) return;
    const scrollTo = pagerItem.getAttribute('data-scrollto');
    if (scrollTo) {
        // remove active class from all pager items
        slider.querySelectorAll('.slider-pager-item').forEach((item)=>{
            item.classList.remove('active');
        });
        // add active class to the clicked pager item
        pagerItem.classList.add('active');
        // scroll the slider to the value of the pager item
        sliderInner.scrollTo({
            left: scrollTo,
            behavior: 'smooth'
        });
    }
}
const $97ae949e493c6975$export$de363e709c412c8a = function(func, limit) {
    let inThrottle;
    return function() {
        const args = arguments;
        const context = this;
        if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(()=>inThrottle = false, limit);
        }
    };
};
/**
 * Normalise the scroll event across browsers
 *
 * @param {HTMLElement} element
 * @param {Function} func
 * @param  {...any} args
 */ const $97ae949e493c6975$export$1db75a395cad5afe = function(element, func, ...args) {
    const context = this;
    if ('onscrollend' in window) element.addEventListener('scrollend', ()=>{
        func.apply(context, args);
    });
    else // fall back to scroll listener with timeout for browsers
    // that don't support scrollend
    element.addEventListener('scroll', ()=>{
        clearTimeout(window.scrollEndTimer);
        window.scrollEndTimer = setTimeout(()=>{
            func.apply(context, args);
        }, 50);
    });
};
// Function to refresh the sliders on the page
const $97ae949e493c6975$export$c02bee88caf14988 = function(sliderElement) {
    const event = new CustomEvent('sliderRefresh', {
        detail: {
            sliderElement: sliderElement
        }
    });
    window.dispatchEvent(event);
};


/**
 * @file pager-factory.js is responsible for creating and rendering the pager
 * for the slider component.
 * @module pagerFactory
 */ /**
 * @typedef {Object} Pager
 * @property {function} init - Initializes the pager with the slider.
 * @property {function} getPageCount - Returns the total number of pages based
 * on the number of items to show.
 * @property {function} render - Renders the pager with the appropriate number
 * of pages.
 * @property {function} addEventListeners - Adds event listeners to the pager.
 * @property {function} setActivePagerItem - Sets the active pager item based
 * on the current slide.
 */ 
const $bf38c4cdf06353f9$var$pager = {};
$bf38c4cdf06353f9$var$pager.init = function(slider) {
    this.slider = slider;
    this.render();
    this.addEventListeners();
};
$bf38c4cdf06353f9$var$pager.getPageCount = function() {
    return Math.ceil(this.slider.elements.length / this.slider.getItemsToShow());
};
$bf38c4cdf06353f9$var$pager.render = function() {
    this.slider.pagerEl.innerHTML = '';
    const itemsToShow = this.slider.getItemsToShow();
    if (itemsToShow === 1) return;
    const elementArray = Array.from(this.slider.elements);
    const pages = this.getPageCount();
    for(let i = 0; i < pages; i++){
        const start = i * itemsToShow;
        const end = start + itemsToShow;
        const sub = elementArray.slice(start, end);
        const dataLeft = sub[0].offsetLeft;
        const d = (0, $97ae949e493c6975$export$dc35b5db7ba3f005)(start, end, this.slider.elements, dataLeft, i);
        this.slider.pagerEl.appendChild(d);
    }
};
$bf38c4cdf06353f9$var$pager.addEventListeners = function() {
    const slider = this.slider;
    this.slider.pagerEl.addEventListener('click', (e)=>{
        const el = e.target;
        (0, $97ae949e493c6975$export$43c35f60672766ee)(el, slider.slider, slider.sliderInner);
    });
};
$bf38c4cdf06353f9$var$pager.setActivePagerItem = function() {
    const slider = this.slider;
    let items = slider.slider.querySelectorAll('.slider-pager-item');
    // convert to array and reverse so that we run backwards from the last
    // page - easiest way to fix the last page bug
    items = Array.from(items);
    items.reverse();
    let activeIsSet = false;
    items.forEach((item)=>{
        activeIsSet = (0, $97ae949e493c6975$export$39e04e72f972b885)(item, slider.elements, activeIsSet, slider.slider);
    });
};
/**
 * Creates a pager object with the given slider.
 * @param {Object} slider - The slider object to create the pager for.
 * @return {Pager} - The pager object.
 */ const $bf38c4cdf06353f9$var$makePager = function(slider) {
    const obj = Object.create($bf38c4cdf06353f9$var$pager);
    obj.init(slider);
    return obj;
};
var $bf38c4cdf06353f9$export$2e2bcd8739ae039 = $bf38c4cdf06353f9$var$makePager;


const $ac7d93ccdc6a4856$var$slider = {};
$ac7d93ccdc6a4856$var$slider.init = function(sliderEl, responsive, hiddenClass = 'hidden') {
    // reference the responsive object
    this.responsive = responsive;
    this.slider = sliderEl;
    this.sliderInner = this.slider.querySelector('.slider-inner');
    this.isSliding = false;
    this.isBtnClick = false;
    // Defensive check for sliderInner
    if (!this.sliderInner) {
        console.warn('Slider inner element not found.');
        return;
    }
    // Nodelist of slider items
    this.elements = this.sliderInner.querySelectorAll('.slider-item');
    // setup elements
    this.buttons = this.slider.querySelector('.slider-nav');
    this.prevButton = this.slider.querySelector('.slider-prev');
    this.nextButton = this.slider.querySelector('.slider-next');
    this.pagerEl = this.slider.querySelector('.slider-pager');
    this.hiddenClass = hiddenClass;
    if (this.pagerEl) this.pager = (0, $bf38c4cdf06353f9$export$2e2bcd8739ae039)(this);
    this.addListeners();
    // Listen for refresh events dispatched from window (pub/sub pattern)
    window.addEventListener('sliderRefresh', (e)=>{
        if (e.detail && e.detail.sliderElement === this.slider) this.refresh();
    });
};
$ac7d93ccdc6a4856$var$slider.moveToPrevious = function() {
    if (this.isSliding) return;
    this.isSliding = true;
    (0, $97ae949e493c6975$export$c1742949d0193489)('previous', this.sliderInner, this.getGap());
};
$ac7d93ccdc6a4856$var$slider.moveToNext = function() {
    if (this.isSliding) return;
    this.isSliding = true;
    (0, $97ae949e493c6975$export$c1742949d0193489)('next', this.sliderInner, this.getGap());
};
$ac7d93ccdc6a4856$var$slider.checkButtons = function() {
    (0, $97ae949e493c6975$export$fb18650d142de48d)(this.sliderInner, this.prevButton, this.nextButton, this.isBtnClick);
    this.isBtnClick = false;
};
$ac7d93ccdc6a4856$var$slider.addListeners = function() {
    if (this.prevButton) this.prevButton.addEventListener('click', ()=>{
        this.isBtnClick = true;
        this.moveToPrevious();
    });
    if (this.nextButton) this.nextButton.addEventListener('click', ()=>{
        this.isBtnClick = true;
        this.moveToNext();
    });
    this._onScrollEnd();
    this._keyboardNavigation();
    this._loadAndResize();
};
$ac7d93ccdc6a4856$var$slider._loadAndResize = function() {
    const setItemSizePromise = (0, $97ae949e493c6975$export$45ee3b73b8783988)((0, $97ae949e493c6975$export$8382a6a3b017d9e2), 50);
    const handler = ()=>{
        setItemSizePromise(this).then((slider)=>{
            slider.showHideButtons();
            slider.checkButtons();
            if (slider.pager) {
                slider.pager.render();
                slider.checkPager();
            }
        }).catch((err)=>{
            console.error(err);
        });
    };
    window.addEventListener('DOMContentLoaded', handler);
    window.addEventListener('resize', handler);
};
$ac7d93ccdc6a4856$var$slider._onScrollEnd = function() {
    const that = this;
    const handleScrollEnd = ()=>{
        that.checkButtons();
        that.isSliding = false;
    };
    const handlePagerOnScroll = ()=>{
        that.checkPager();
    };
    // throttled scroll listener
    const throttledScrollHandler = (0, $97ae949e493c6975$export$de363e709c412c8a)(handlePagerOnScroll, 50);
    this.sliderInner.addEventListener('scroll', throttledScrollHandler);
    (0, $97ae949e493c6975$export$1db75a395cad5afe)(this.sliderInner, handleScrollEnd);
};
$ac7d93ccdc6a4856$var$slider._keyboardNavigation = function() {
    const that = this;
    this.sliderInner.addEventListener('keydown', function(e) {
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            that.moveToPrevious();
        }
        if (e.key === 'ArrowRight') {
            e.preventDefault();
            that.moveToNext();
        }
        if (e.key === 'Home') {
            e.preventDefault();
            that.sliderInner.scrollTo({
                left: 0,
                behavior: 'smooth'
            });
        }
        if (e.key === 'End') {
            e.preventDefault();
            that.sliderInner.scrollTo({
                left: that.sliderInner.scrollWidth,
                behavior: 'smooth'
            });
        }
    });
};
$ac7d93ccdc6a4856$var$slider.showHideButtons = function() {
    if (this.buttons != undefined) {
        const containerWidth = this.sliderInner.clientWidth;
        if (containerWidth >= this.tempTotalWidth) this.buttons.classList.add(this.hiddenClass);
        else this.buttons.classList.remove(this.hiddenClass);
    }
};
$ac7d93ccdc6a4856$var$slider.getGap = function() {
    const opts = (0, $97ae949e493c6975$export$9266a1443ac0612)(this.responsive);
    return opts['gutter'];
};
$ac7d93ccdc6a4856$var$slider.getItemsToShow = function() {
    const opts = (0, $97ae949e493c6975$export$9266a1443ac0612)(this.responsive);
    return opts['items'];
};
$ac7d93ccdc6a4856$var$slider.getPreview = function() {
    const opts = (0, $97ae949e493c6975$export$9266a1443ac0612)(this.responsive);
    if (opts['preview']) return parseInt(opts['preview'], 10);
    return 0;
};
$ac7d93ccdc6a4856$var$slider.checkPager = function() {
    if (this.pager) {
        if (this.pager.getPageCount() === 1) this.pagerEl.classList.add(this.hiddenClass);
        else this.pagerEl.classList.remove(this.hiddenClass);
        this.slider.querySelectorAll('.slider-pager-item').forEach((sliderItem)=>{
            sliderItem.classList.remove('active');
        });
        this.pager.setActivePagerItem();
    }
};
/**
 * Refresh the slider after filtering or modifying slider items.
 * Re-queries visible slider items, recalculates positions, updates button
 * states, and re-renders the pager.
 *
 * @example
 * // Filter items and refresh
 * document.querySelectorAll('.slider-item').forEach(item => {
 *   if (shouldShow(item)) {
 *     item.classList.remove('hidden');
 *   } else {
 *     item.classList.add('hidden');
 *   }
 * });
 * sliderInstance.refresh();
 */ $ac7d93ccdc6a4856$var$slider.refresh = function() {
    // Re-query slider items to pick up any filtered elements
    // Only include items that are not hidden (have display !== 'none')
    const allItems = this.sliderInner.querySelectorAll('.slider-item');
    const visibleItems = Array.from(allItems).filter((item)=>{
        const style = window.getComputedStyle(item);
        return style.display !== 'none';
    });
    // Update the elements NodeList to only include visible items
    this.elements = visibleItems;
    // Recalculate and update item sizes
    (0, $97ae949e493c6975$export$8382a6a3b017d9e2)(this);
    // Update button visibility
    this.showHideButtons();
    // Check button states
    this.checkButtons();
    // Re-render pager and check state if pager exists
    if (this.pager) {
        this.pager.render();
        this.checkPager();
    }
};
const $ac7d93ccdc6a4856$var$makeSlider = function(sliderElement, responsive, hiddenClass) {
    const obj = Object.create($ac7d93ccdc6a4856$var$slider);
    obj.init(sliderElement, responsive, hiddenClass);
    return obj;
};
var $ac7d93ccdc6a4856$export$2e2bcd8739ae039 = $ac7d93ccdc6a4856$var$makeSlider;



/**
 * slider is just a wrapper function that initialises the slider
 * based on the options passed to it.
 * It also sets the default options for the slider.
 *
 * Users can pass their own options to the slider function and override
 * the default options to customise the slider as required.
 *
 * @param {Object} Options object
 * @returns {void}
 */ const $9d58f3e588a50717$var$slider = function({ container: container = '.slider', responsive: responsive = {
    0: {
        items: 2,
        gutter: 16
    },
    576: {
        items: 3,
        gutter: 16
    },
    768: {
        items: 4,
        gutter: 16
    },
    992: {
        items: 5,
        gutter: 16
    },
    1200: {
        items: 6,
        gutter: 16
    }
}, hiddenClass: hiddenClass = 'hidden' } = {}) {
    /**
   * Get a list of all the slider items on the page
   */ const sliders = document.querySelectorAll(container);
    window.sliderRefresh = window.sliderRefresh || (0, $97ae949e493c6975$export$c02bee88caf14988);
    /**
   * Initialise sliders from a NodeList based on the current selector
   */ sliders.forEach((sliderEl)=>{
        (0, $ac7d93ccdc6a4856$export$2e2bcd8739ae039)(sliderEl, responsive, hiddenClass);
    });
};
var $9d58f3e588a50717$export$2e2bcd8739ae039 = $9d58f3e588a50717$var$slider;


window.slider = (0, $9d58f3e588a50717$export$2e2bcd8739ae039);

})();
//# sourceMappingURL=index.js.map
